import React, { useState, useMemo } from 'react';
import { AssignedCamera } from 'core/models/layout';
import { IconButton, SxProps, Theme } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { CameraList } from 'components/views/atoms/CameraList';
import { CameraDevice, MediaDevice } from 'core/models/mediaDevice';
import { LabeledCheckbox } from 'components/views/atoms/LabeledCheckbox';
import { VideoStreamConfigForm } from '../VideoStreamConfigForm';
import { SafeBox } from 'components/views/atoms/SafeBox';

const styles = {
    frame: {
        margin: theme => theme.spacing(1),
        padding: theme => theme.spacing(1),
        border: 'thin solid',
        borderRadius: theme => theme.shape.borderRadius,
        borderColor: 'transparent',
        '&:hover': {
            borderColor: theme => theme.palette.divider,
        },
    } as SxProps<Theme>,
    select: {
        width: '50ex',
    } as SxProps<Theme>,
    clearIndicator: {
        color: theme => theme.palette.action.active,
        visibility: 'visible',
    } as SxProps<Theme>,
    horizontal: {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
    } as SxProps<Theme>,
    vertical: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    } as SxProps<Theme>,
    cameraOptions: {
        marginLeft: theme => theme.spacing(2),
    } as SxProps<Theme>,
    buttonBox: {
        margin: theme => theme.spacing(2),
    } as SxProps<Theme>,
    labeledSelect: {
        marginLeft: '1ex',
    } as SxProps<Theme>,
    duplicateMarker: {
        color: theme => theme.palette.error.main,
        margin: theme => theme.spacing(1),
        fontFamily: theme => theme.typography.caption.fontFamily,
    } as SxProps<Theme>,
    unpluggedMarker: {
        color: theme => theme.palette.error.main,
        margin: theme => theme.spacing(1),
        fontFamily: theme => theme.typography.caption.fontFamily,
    } as SxProps<Theme>,
};

export type CameraSettingFormProps = {
    className?: string;
    assignedCamera: AssignedCamera;
    duplicate: boolean;
    cameras: CameraDevice[] | undefined;
    isRemovable?: boolean;
    onChange: (assigned: AssignedCamera) => void;
    onRemove?: () => void;
    getVideoConfig?: (mediaDeviceId: MediaDevice.Id) => Promise<Partial<MediaDevice.VideoConfig>>;
    putVideoConfig?: (mediaDeviceId: MediaDevice.Id, config: Partial<MediaDevice.VideoConfig>) => Promise<void>;
}

export function CameraSettingForm(props: CameraSettingFormProps) {
    const { className, assignedCamera, duplicate, cameras, isRemovable, onChange, onRemove, getVideoConfig, putVideoConfig } = props;

    const camera = useMemo(() => cameras?.find(c => c.tag == assignedCamera.tag), [cameras, assignedCamera.tag]);
    const unplugged = assignedCamera.tag !== undefined && camera === undefined;

    const [isHovering, setHovering] = useState(false);

    return (
        <SafeBox className={className} sx={styles.frame} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
            <SafeBox sx={styles.horizontal}>
                <SafeBox sx={styles.buttonBox} visibility={(isHovering && isRemovable !== false) ? 'visible' : 'hidden'}>
                    <IconButton onClick={onRemove} size='small' >
                        <Delete />
                    </IconButton>
                </SafeBox>
                <SafeBox sx={styles.vertical}>
                    <SafeBox sx={styles.horizontal}>
                        <CameraList
                            styles={styles}
                            unplugged={unplugged}
                            selected={assignedCamera}
                            options={cameras}
                            onChange={assigned => {
                                const tmp = { ...assignedCamera };
                                delete tmp.tag;
                                onChange({ ...tmp, ...assigned });
                            }}
                            autoComplete={true}
                        />
                        <SafeBox>
                            {
                                duplicate == true
                                    ? <SafeBox sx={styles.duplicateMarker}>duplicate</SafeBox>
                                    : <React.Fragment />
                            }
                            {
                                unplugged == true
                                    ? <SafeBox sx={styles.unpluggedMarker}>unplugged</SafeBox>
                                    : <React.Fragment />
                            }
                        </SafeBox>
                    </SafeBox>
                    <SafeBox sx={styles.cameraOptions}>
                        <SafeBox sx={styles.vertical}>
                            <SafeBox sx={styles.horizontal}>
                                <LabeledCheckbox
                                    label='mirror'
                                    disabled={!assignedCamera.tag}
                                    checked={assignedCamera.mirror ?? false}
                                    onChange={mirror => onChange({ ...assignedCamera, mirror })} />
                                <LabeledCheckbox
                                    label='upsidedown'
                                    disabled={!assignedCamera.tag}
                                    checked={assignedCamera.upsidedown ?? false}
                                    onChange={upsidedown => onChange({ ...assignedCamera, upsidedown })} />
                            </SafeBox>
                            <VideoStreamConfigForm
                                camera={camera}
                                disabled={!assignedCamera.tag || !putVideoConfig}
                                getVideoConfig={getVideoConfig}
                                putVideoConfig={putVideoConfig}
                            />
                        </SafeBox>
                    </SafeBox>
                </SafeBox>
            </SafeBox>
        </SafeBox>
    );
}
